import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Form, Alert, Spinner, Toast } from 'react-bootstrap';
import { FaBell, FaHome, FaChartLine, FaEnvelope, FaSms, FaMapMarkerAlt, FaCheck, FaSignInAlt, FaLink, FaPhone } from 'react-icons/fa';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash';
import axios from 'axios';
import './Styles.css';
import { handleTokenAndPostRequest, trackEvent } from '../utils/utils';
import { useAgentDetails } from '../utils/useAgentDetails';
import SignInModal from '../components/SignInModal';

// Serialise just the user-editable preferences (no metadata like
// landing_page_slug or updated_at) so we can detect whether settings
// actually changed and skip redundant saves on (re)hydration.
function serializeSettings({
  propertyUpdates, propertyFrequency, selectedProperties,
  marketUpdates, marketFrequency, selectedSuburbs,
  channels, smsNumber, contactEmail, isMobileUser,
}) {
  return JSON.stringify({
    property_updates: {
      enabled: propertyUpdates,
      frequency: propertyFrequency,
      selected_properties: selectedProperties.map(p => ({ label: p.label, value: p.value })),
    },
    market_updates: {
      enabled: marketUpdates,
      frequency: marketFrequency,
      selected_suburbs: selectedSuburbs.map(s => ({ label: s.label, value: s.value })),
    },
    delivery: {
      channels,
      sms_number: channels.includes('sms') ? smsNumber : '',
      contact_email: channels.includes('email') && isMobileUser ? contactEmail : '',
    },
  });
}

function Notifications() {
  // Auth & UI state
  const [noTokenMessage, setNoTokenMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [email, setEmail] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [showSignInModal, setShowSignInModal] = useState(false);
  // Serialised snapshot of the last loaded/saved settings. Used to skip
  // redundant auto-saves when state is hydrated on load (or re-hydrated
  // once agentDetails arrives), since that isn't a real user change.
  const lastSavedRef = useRef(null);

  // Notification type toggles
  const [propertyUpdates, setPropertyUpdates] = useState(true);
  const [marketUpdates, setMarketUpdates] = useState(true);

  // Property updates settings
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [propertySelectKey, setPropertySelectKey] = useState(0);

  // Market updates settings
  const [selectedSuburbs, setSelectedSuburbs] = useState([]);
  const [suburbSelectKey, setSuburbSelectKey] = useState(0);

  // Per-type frequency
  const [propertyFrequency, setPropertyFrequency] = useState('monthly');
  const [marketFrequency, setMarketFrequency] = useState('monthly');

  // Channel (can select both)
  const [channels, setChannels] = useState(['email']);
  const [smsNumber, setSmsNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const { agent_slug, agentDetails, branding } = useAgentDetails();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const param_token = params.get('token') || localStorage.getItem('param_token');

  const { accentColor, buttonTextColor, isLight, iconColor, iconBgColor } = branding || {};
  const isLoggedOut = !localStorage.getItem('jwt_token') && !!param_token;

  // Detect if the user's identity is a mobile number (from SMS campaign token)
  const isMobileUser = email && /^\+?\d[\d\s-]{7,}$/.test(email) && !email.includes('@');

  // Authenticate & load settings
  useEffect(() => {
    const init = async () => {
      const jwtToken = localStorage.getItem('jwt_token');
      const hasToken = jwtToken || param_token;
      if (!hasToken) {
        setNoTokenMessage('Please use the link from your email to access your notification settings.');
        setSettingsLoading(false);
        return;
      }

      // Track the values we apply so we can record them as the baseline
      // (defaults mirror the initial useState values).
      let nextEmail = null;
      let nextPropertyUpdates = true;
      let nextPropertyFrequency = 'monthly';
      let nextSelectedProperties = [];
      let nextMarketUpdates = true;
      let nextMarketFrequency = 'monthly';
      let nextSelectedSuburbs = [];
      let nextChannels = ['email'];
      let nextSmsNumber = '';
      let nextContactEmail = '';

      // If logged in, authenticate to get email
      if (jwtToken) {
        try {
          const authResponse = await handleTokenAndPostRequest(
            `${window.api_host}/authenticate-token`,
            {},
          );
          if (authResponse.data.status !== 'error' && authResponse.data.email) {
            nextEmail = authResponse.data.email;
            setEmail(nextEmail);
          } else {
            setNoTokenMessage(authResponse.data.message || 'Your session has expired. Please sign in again.');
            setSettingsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error authenticating token:', error);
          setNoTokenMessage('Failed to authenticate. Please try again later.');
          setSettingsLoading(false);
          return;
        }
      } else if (agentDetails?.user_email) {
        // Use email from agentDetails for param_token users
        nextEmail = agentDetails.user_email;
        setEmail(nextEmail);
        // Pre-fill SMS number if the identifier is a mobile number
        const ue = agentDetails.user_email;
        if (ue && /^\+?\d[\d\s-]{7,}$/.test(ue) && !ue.includes('@')) {
          nextSmsNumber = ue.replace(/^\+61/, '0');
          nextChannels = ['sms'];
          setSmsNumber(nextSmsNumber);
          setChannels(nextChannels);
        }
      }

      // Fetch existing settings (works with either JWT or param_token)
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/read-user-settings`,
          { agent_slug: agent_slug || agentDetails?.landing_page_slug || null },
        );
        const data = response.data;
        // Support both new nested and legacy flat structures
        const propUpdates = data.property_updates;
        const mktUpdates = data.market_updates;
        const delivery = data.delivery;

        if (propUpdates && typeof propUpdates === 'object' && 'enabled' in propUpdates) {
          // New nested structure
          nextPropertyUpdates = propUpdates.enabled ?? true;
          nextPropertyFrequency = propUpdates.frequency || 'monthly';
          if (Array.isArray(propUpdates.selected_properties) && propUpdates.selected_properties.length > 0) {
            nextSelectedProperties = propUpdates.selected_properties;
          }
          nextMarketUpdates = mktUpdates?.enabled ?? true;
          nextMarketFrequency = mktUpdates?.frequency || 'monthly';
          if (Array.isArray(mktUpdates?.selected_suburbs) && mktUpdates.selected_suburbs.length > 0) {
            nextSelectedSuburbs = mktUpdates.selected_suburbs;
          }
          if (delivery) {
            if (Array.isArray(delivery.channels)) nextChannels = delivery.channels;
            nextSmsNumber = delivery.sms_number || '';
            nextContactEmail = delivery.contact_email || '';
          }
        } else {
          // Legacy flat structure
          nextMarketUpdates = data.suburb_market_updates ?? true;
          nextPropertyUpdates = data.property_updates ?? true;
          nextPropertyFrequency = data.property_frequency || data.frequency || 'monthly';
          nextMarketFrequency = data.market_frequency || data.frequency || 'monthly';
          if (Array.isArray(data.channels)) {
            nextChannels = data.channels;
          } else if (data.channel) {
            nextChannels = [data.channel];
          }
          nextSmsNumber = data.sms_number || '';
          if (Array.isArray(data.selected_suburbs) && data.selected_suburbs.length > 0) {
            nextSelectedSuburbs = data.selected_suburbs;
          }
          if (Array.isArray(data.selected_properties) && data.selected_properties.length > 0) {
            nextSelectedProperties = data.selected_properties;
          }
        }

        // Apply the loaded values to state
        setPropertyUpdates(nextPropertyUpdates);
        setPropertyFrequency(nextPropertyFrequency);
        setSelectedProperties(nextSelectedProperties);
        setMarketUpdates(nextMarketUpdates);
        setMarketFrequency(nextMarketFrequency);
        setSelectedSuburbs(nextSelectedSuburbs);
        setChannels(nextChannels);
        setSmsNumber(nextSmsNumber);
        setContactEmail(nextContactEmail);
      } catch (error) {
        console.error('Error fetching user settings:', error);
        if (!jwtToken) {
          setNoTokenMessage('Unable to load your settings. Your link may have expired.');
        }
      } finally {
        setSettingsLoading(false);
        // Record the loaded settings as the auto-save baseline so hydration
        // (including the re-run once agentDetails arrives) isn't mistaken
        // for a user edit.
        const mobile = !!nextEmail && /^\+?\d[\d\s-]{7,}$/.test(nextEmail) && !nextEmail.includes('@');
        lastSavedRef.current = serializeSettings({
          propertyUpdates: nextPropertyUpdates,
          propertyFrequency: nextPropertyFrequency,
          selectedProperties: nextSelectedProperties,
          marketUpdates: nextMarketUpdates,
          marketFrequency: nextMarketFrequency,
          selectedSuburbs: nextSelectedSuburbs,
          channels: nextChannels,
          smsNumber: nextSmsNumber,
          contactEmail: nextContactEmail,
          isMobileUser: mobile,
        });
      }
    };
    init();
  }, [param_token, agentDetails]);

  // Save settings (called automatically on change)
  const saveSettings = useCallback(async () => {
    const hasAuth = email || param_token;
    if (!hasAuth) return;

    setIsSaving(true);
    setNoTokenMessage('');
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/save-user-settings`,
        {
          user_settings: {
            landing_page_slug: agentDetails?.landing_page_slug,
            updated_at: Math.floor(Date.now() / 1000),
            property_updates: {
              enabled: propertyUpdates,
              frequency: propertyFrequency,
              selected_properties: selectedProperties.map(p => ({ label: p.label, value: p.value })),
            },
            market_updates: {
              enabled: marketUpdates,
              frequency: marketFrequency,
              selected_suburbs: selectedSuburbs.map(s => ({ label: s.label, value: s.value })),
            },
            delivery: {
              channels,
              sms_number: channels.includes('sms') ? smsNumber : '',
              contact_email: channels.includes('email') && isMobileUser ? contactEmail : '',
            },
          },
        },
      );
      if (response.data.status === 'success') {
        setToastVariant('success');
        setToastMessage('Settings saved');
        setShowToast(true);
      } else {
        setToastVariant('danger');
        setToastMessage('Failed to save settings');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setToastVariant('danger');
      setToastMessage('Failed to save. Please try again.');
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  }, [email, param_token, agentDetails, propertyUpdates, propertyFrequency, selectedProperties, marketUpdates, marketFrequency, selectedSuburbs, channels, smsNumber, contactEmail]);

  // Debounced auto-save ref
  const debouncedSaveRef = useRef(null);
  useEffect(() => {
    debouncedSaveRef.current = saveSettings;
  }, [saveSettings]);

  // Auto-save whenever the user changes settings (debounced). Saves are
  // skipped while loading and whenever the current values match the last
  // loaded/saved baseline, so hydration never triggers a redundant save.
  useEffect(() => {
    if (settingsLoading) return;
    const snapshot = serializeSettings({
      propertyUpdates, propertyFrequency, selectedProperties,
      marketUpdates, marketFrequency, selectedSuburbs,
      channels, smsNumber, contactEmail, isMobileUser,
    });
    // Baseline not yet primed (e.g. settings failed to load): adopt the
    // current values without saving.
    if (lastSavedRef.current === null) {
      lastSavedRef.current = snapshot;
      return;
    }
    if (snapshot === lastSavedRef.current) return;
    const timer = setTimeout(() => {
      lastSavedRef.current = snapshot;
      debouncedSaveRef.current?.();
    }, 1200);
    return () => clearTimeout(timer);
  }, [settingsLoading, propertyUpdates, propertyFrequency, selectedProperties, marketUpdates, marketFrequency, selectedSuburbs, channels, smsNumber, contactEmail, isMobileUser]);

  // Search loaders
  const loadSuburbs = useCallback(
    debounce((inputValue, callback) => {
      if (!inputValue || inputValue.length < 2) { callback([]); return; }
      axios.get(`${window.api_host}/location-search?q=${encodeURIComponent(inputValue)}`)
        .then(response => {
          callback(response.data.filter(r => r.type === 'suburb').map(s => ({ label: s.label, value: s.value })));
        })
        .catch(() => callback([]));
    }, 500),
    []
  );

  const loadProperties = useCallback(
    debounce((inputValue, callback) => {
      if (!inputValue || inputValue.length < 3) { callback([]); return; }
      axios.get(`${window.api_host}/property-search?q=${encodeURIComponent(inputValue)}`)
        .then(response => {
          callback(response.data.map(p => ({ label: p.address_text, value: p.id })));
        })
        .catch(() => callback([]));
    }, 500),
    []
  );

  // Shared styles
  const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: 16,
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
    padding: '28px 28px',
    marginBottom: 20,
  };

  const sectionLabelStyle = {
    fontSize: '0.72rem',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    fontWeight: 600,
    marginBottom: 12,
  };

  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
  ];

  const channelOptions = [
    { value: 'email', label: 'Email', icon: <FaEnvelope size={14} /> },
    { value: 'sms', label: 'SMS', icon: <FaSms size={14} /> },
  ];

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 12,
      borderColor: state.isFocused ? accentColor : '#e5e7eb',
      boxShadow: state.isFocused ? `0 0 0 1px ${accentColor}` : 'none',
      minHeight: 44,
      '&:hover': { borderColor: accentColor },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: `${iconBgColor}14`,
      borderRadius: 8,
      border: `1px solid ${accentColor}30`,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#1a1a1a',
      fontSize: '0.85rem',
      fontWeight: 500,
      padding: '2px 6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#9ca3af',
      borderRadius: '0 8px 8px 0',
      '&:hover': { backgroundColor: '#fef2f2', color: '#dc2626' },
    }),
    placeholder: (provided) => ({ ...provided, textAlign: 'left', color: '#c4c4c4' }),
    menu: (provided) => ({ ...provided, textAlign: 'left', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? `${accentColor}0a` : '#fff',
      color: '#1a1a1a',
      fontSize: '0.9rem',
      '&:active': { backgroundColor: `${accentColor}18` },
    }),
  };

  if (settingsLoading || !agentDetails) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!email && !param_token && !settingsLoading) {
    return (
      <Container style={{ maxWidth: 560, paddingTop: 120, paddingBottom: 60 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 18, backgroundColor: `${iconBgColor}14`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
          }}>
            <FaBell size={28} style={{ color: iconColor }} />
          </div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#111', marginBottom: 8, letterSpacing: '-0.02em' }}>
            Notification Settings
          </h1>
          <p style={{ fontSize: '0.92rem', color: '#6b7280', marginBottom: 0, lineHeight: 1.5 }}>
            Sign in or use the link from your email to manage your notification preferences.
          </p>
        </div>

        <div style={{
          backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          padding: '32px 28px', textAlign: 'center',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              type="button"
              onClick={() => setShowSignInModal(true)}
              style={{
                width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
                backgroundColor: accentColor, color: buttonTextColor,
                fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                boxShadow: `0 4px 16px ${accentColor}30`,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <FaSignInAlt size={16} /> Sign In
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0' }}>
              <div style={{ flex: 1, height: 1, backgroundColor: '#e5e7eb' }} />
              <span style={{ fontSize: '0.78rem', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}>or</span>
              <div style={{ flex: 1, height: 1, backgroundColor: '#e5e7eb' }} />
            </div>

            <div style={{
              padding: '16px 20px', borderRadius: 12, backgroundColor: '#f9fafb',
              border: '1px solid #f3f4f6',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 6 }}>
                <FaLink size={13} style={{ color: '#9ca3af' }} />
                <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#374151' }}>Use your email link</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: 0, lineHeight: 1.5 }}>
                Check your inbox for an email with a direct link to your notification settings.
              </p>
            </div>
          </div>
        </div>

        {agentDetails && (
          <SignInModal
            show={showSignInModal}
            onHide={() => setShowSignInModal(false)}
            onSuccess={() => { setShowSignInModal(false); window.location.reload(); }}
            agentDetails={agentDetails}
          />
        )}
      </Container>
    );
  }

  if (!email && !param_token && noTokenMessage) {
    return (
      <Container style={{ maxWidth: 560, paddingTop: 120, paddingBottom: 60 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, backgroundColor: `${iconBgColor}14`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
          }}>
            <FaBell size={24} style={{ color: iconColor }} />
          </div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#111', marginBottom: 6, letterSpacing: '-0.02em' }}>
            Notification Settings
          </h1>
        </div>
        <Alert variant="warning" style={{ borderRadius: 12, border: 'none', textAlign: 'center' }}>
          {noTokenMessage}
        </Alert>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: 560, paddingTop: 120, paddingBottom: 60 }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 16, backgroundColor: `${iconBgColor}14`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <FaBell size={24} style={{ color: iconColor }} />
        </div>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#111', marginBottom: 6, letterSpacing: '-0.02em' }}>
          Notification Settings
        </h1>
        <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: 0 }}>
          Choose what updates you'd like to receive and how.
        </p>
        {email && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            backgroundColor: '#f3f4f6', borderRadius: 20, padding: '6px 16px',
            fontSize: '0.82rem', color: '#4b5563', fontWeight: 500, marginTop: 12,
          }}>
            {isMobileUser
              ? <><FaPhone size={11} style={{ color: '#9ca3af' }} /> {email.replace(/^\+61/, '0')}</>
              : <><FaEnvelope size={11} style={{ color: '#9ca3af' }} /> {email}</>
            }
          </div>
        )}
      </div>

      <Form>
        {/* ─── Property Updates ─── */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: propertyUpdates ? 20 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                backgroundColor: propertyUpdates ? `${iconBgColor}14` : '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.2s',
              }}>
                <FaHome size={16} style={{ color: propertyUpdates ? accentColor : '#9ca3af' }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111' }}>Property Updates</div>
                <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Get notified about your tracked properties</div>
              </div>
            </div>
            <Form.Check
              type="switch"
              checked={propertyUpdates}
              onChange={e => setPropertyUpdates(e.target.checked)}
              className="custom-switch"
              style={{ '--agent-colour': accentColor }}
            />
          </div>

          {propertyUpdates && (
            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 20 }}>
              <div style={sectionLabelStyle}>
                <FaMapMarkerAlt size={10} style={{ marginRight: 6 }} />
                Properties to track
              </div>
              <AsyncSelect
                key={`props-${propertySelectKey}`}
                isMulti
                cacheOptions
                loadOptions={loadProperties}
                value={selectedProperties}
                onChange={setSelectedProperties}
                placeholder="Search by address..."
                noOptionsMessage={({ inputValue }) => inputValue && inputValue.length >= 3 ? 'No properties found' : 'Type at least 3 characters'}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />

              <div style={{ ...sectionLabelStyle, marginTop: 20 }}>Frequency</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {frequencyOptions.map(opt => {
                  const active = propertyFrequency === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setPropertyFrequency(opt.value)}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 10,
                        border: `1.5px solid ${active ? accentColor : '#e5e7eb'}`,
                        backgroundColor: active ? `${accentColor}0a` : '#fff',
                        color: active ? iconColor : '#6b7280',
                        fontWeight: active ? 600 : 500, fontSize: '0.88rem',
                        cursor: 'pointer', transition: 'all 0.15s ease', position: 'relative',
                      }}
                    >
                      {active && <FaCheck size={8} style={{ position: 'absolute', top: 6, right: 8, color: iconColor }} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ─── Market Updates ─── */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: marketUpdates ? 20 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                backgroundColor: marketUpdates ? `${iconBgColor}14` : '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.2s',
              }}>
                <FaChartLine size={16} style={{ color: marketUpdates ? accentColor : '#9ca3af' }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111' }}>Market Updates</div>
                <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Suburb market trends and insights</div>
              </div>
            </div>
            <Form.Check
              type="switch"
              checked={marketUpdates}
              onChange={e => setMarketUpdates(e.target.checked)}
              className="custom-switch"
              style={{ '--agent-colour': accentColor }}
            />
          </div>

          {marketUpdates && (
            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 20 }}>
              <div style={sectionLabelStyle}>
                <FaMapMarkerAlt size={10} style={{ marginRight: 6 }} />
                Suburbs to follow
              </div>
              <AsyncSelect
                key={`subs-${suburbSelectKey}`}
                isMulti
                cacheOptions
                loadOptions={loadSuburbs}
                value={selectedSuburbs}
                onChange={setSelectedSuburbs}
                placeholder="Search by suburb or postcode..."
                noOptionsMessage={({ inputValue }) => inputValue && inputValue.length >= 2 ? 'No suburbs found' : 'Type at least 2 characters'}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />

              <div style={{ ...sectionLabelStyle, marginTop: 20 }}>Frequency</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {frequencyOptions.map(opt => {
                  const active = marketFrequency === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setMarketFrequency(opt.value)}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 10,
                        border: `1.5px solid ${active ? accentColor : '#e5e7eb'}`,
                        backgroundColor: active ? `${accentColor}0a` : '#fff',
                        color: active ? iconColor : '#6b7280',
                        fontWeight: active ? 600 : 500, fontSize: '0.88rem',
                        cursor: 'pointer', transition: 'all 0.15s ease', position: 'relative',
                      }}
                    >
                      {active && <FaCheck size={8} style={{ position: 'absolute', top: 6, right: 8, color: iconColor }} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ─── Delivery Preferences ─── */}
        {(propertyUpdates || marketUpdates) && (
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111', marginBottom: 20 }}>
              Delivery Preferences
            </div>

            {/* Channel */}
            <div>
              <div style={sectionLabelStyle}>Notification Channels</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {channelOptions.map(opt => {
                  const active = channels.includes(opt.value);
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => {
                        setChannels(prev =>
                          prev.includes(opt.value)
                            ? prev.filter(c => c !== opt.value)
                            : [...prev, opt.value]
                        );
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 0',
                        borderRadius: 10,
                        border: `1.5px solid ${active ? accentColor : '#e5e7eb'}`,
                        backgroundColor: active ? `${accentColor}0a` : '#fff',
                        color: active ? iconColor : '#6b7280',
                        fontWeight: active ? 600 : 500,
                        fontSize: '0.88rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        position: 'relative',
                      }}
                    >
                      {active && <FaCheck size={8} style={{ position: 'absolute', top: 6, right: 8, color: iconColor }} />}
                      {opt.icon} {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* SMS number input */}
              {channels.includes('sms') && (
                <div style={{ marginTop: 14 }}>
                  <Form.Control
                    type="tel"
                    placeholder="Enter your mobile number"
                    value={smsNumber}
                    onChange={e => setSmsNumber(e.target.value)}
                    style={{
                      borderRadius: 10,
                      borderColor: '#e5e7eb',
                      padding: '10px 14px',
                      fontSize: '0.9rem',
                    }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 6 }}>
                    We'll send SMS notifications to this number.
                  </div>
                </div>
              )}

              {/* Email input for mobile-identified users selecting email channel */}
              {isMobileUser && channels.includes('email') && (
                <div style={{ marginTop: 14 }}>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email address"
                    value={contactEmail}
                    onChange={e => setContactEmail(e.target.value)}
                    style={{
                      borderRadius: 10,
                      borderColor: '#e5e7eb',
                      padding: '10px 14px',
                      fontSize: '0.9rem',
                    }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 6 }}>
                    We'll send email notifications to this address.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Auto-save indicator */}
        {isSaving && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', color: '#9ca3af', fontSize: '0.82rem' }}>
            <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> Saving...
          </div>
        )}

        {noTokenMessage && (
          <Alert variant="danger" className="mt-3" style={{ borderRadius: 12, border: 'none' }}>
            {noTokenMessage}
          </Alert>
        )}
      </Form>

      {/* Toast notification */}
      <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={2500}
          autohide
          style={{
            borderRadius: 12,
            border: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            backgroundColor: toastVariant === 'success' ? '#f0fdf4' : '#fef2f2',
            color: toastVariant === 'success' ? '#16a34a' : '#dc2626',
            minWidth: 200,
          }}
        >
          <Toast.Body style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: '0.88rem', justifyContent: 'center' }}>
            <FaCheck size={12} /> {toastMessage}
          </Toast.Body>
        </Toast>
      </div>
    </Container>
  );
}

export default Notifications;